import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  const navigationItems = [
    { name: 'Acerca de', href: '/acerca-de' },
    { name: 'Menú', href: '/menu' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contacto', href: '/contacto' }
  ];

  // Determine if a navigation item is active
  const isActiveRoute = (href: string) => {
    if (href === '/blog') {
      return location.pathname === '/' || location.pathname === '/blog' || location.pathname.startsWith('/blog/');
    }
    return location.pathname === href;
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking on a navigation link
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scrolling when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header 
      ref={headerRef}
      className={`header ${isScrolled ? 'header--scrolled' : ''} ${isMobileMenuOpen ? 'header--mobile-menu-open' : ''} ${className}`}
    >
      <div className="header__container">
        {/* Logo */}
        <div className="header__logo">
          <Link to="/" className="header__logo-link">
            <img 
              src="/alere-logo-56586a.png" 
              alt="Alere Café"
              className="header__logo-img"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="header__nav" aria-label="Navegación principal">
          <ul className="header__nav-list">
            {navigationItems.map((item) => {
              const isActive = isActiveRoute(item.href);
              return (
                <li key={item.name} className="header__nav-item">
                  <Link 
                    to={item.href} 
                    className={`header__nav-link ${isActive ? 'header__nav-link--active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile menu button */}
        <button 
          className={`header__mobile-toggle ${isMobileMenuOpen ? 'header__mobile-toggle--open' : ''}`}
          aria-label={isMobileMenuOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
          aria-expanded={isMobileMenuOpen}
          onClick={toggleMobileMenu}
        >
          <span className="header__mobile-toggle-line"></span>
          <span className="header__mobile-toggle-line"></span>
          <span className="header__mobile-toggle-line"></span>
        </button>

        {/* Mobile navigation menu */}
        <nav 
          className={`header__mobile-nav ${isMobileMenuOpen ? 'header__mobile-nav--open' : ''}`}
          aria-label="Navegación móvil"
        >
          <ul className="header__mobile-nav-list">
            {navigationItems.map((item) => {
              const isActive = isActiveRoute(item.href);
              return (
                <li key={item.name} className="header__mobile-nav-item">
                  <Link 
                    to={item.href}
                    className={`header__mobile-nav-link ${isActive ? 'header__mobile-nav-link--active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={closeMobileMenu}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;